import {
  DeleteOutlined,
  EditOutlined,
  LinkedinOutlined,
  MailOutlined,
  PlusOutlined,
  TwitterOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUpload from '../components/ImageUpload';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { TextArea, Search } = Input;
const { Option } = Select;

interface Speaker {
  id: string;
  name: string;
  order?: number | null;
  title?: string;
  company?: string;
  bio?: string;
  photo?: string;
  email?: string;
  linkedin?: string;
  twitter?: string;
  eventId: string;
  Event?: {
    id: string;
    title: string;
    Organization?: {
      name: string;
    };
  };
}

interface Event {
  id: string;
  title: string;
  organizationId: string;
}

const Speakers: React.FC = () => {
  const { t } = useTranslation();
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState<Speaker | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const { user } = useAuthStore();

  useEffect(() => {
    fetchEvents();
    fetchSpeakers();
  }, []);

  const fetchEvents = async () => {
    try {
      const params: any = {};
      if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }
      const response = await api.get('/events', { params });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchSpeakers = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedEvent) {
        params.eventId = selectedEvent;
      } else if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }

      const response = await api.get('/speakers', { params });
      setSpeakers(response.data || []);
    } catch (error) {
      message.error(t('speakers.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpeakers();
  }, [selectedEvent]);

  const handleCreate = () => {
    setEditingSpeaker(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Speaker) => {
    setEditingSpeaker(record);
    form.setFieldsValue({
      ...record,
      eventId: record.eventId || record.Event?.id,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/speakers/${id}`);
      message.success(t('speakers.deleted'));
      fetchSpeakers();
    } catch (error) {
      message.error(t('messages.delete_error'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      console.log('Submitting speaker data:', values);
      if (editingSpeaker) {
        // Remove eventId from update data as it cannot be changed
        const { eventId, ...updateData } = values;
        await api.patch(`/speakers/${editingSpeaker.id}`, updateData);
        message.success(t('speakers.updated'));
      } else {
        await api.post('/speakers', values);
        message.success(t('speakers.created'));
      }
      setModalVisible(false);
      fetchSpeakers();
    } catch (error: any) {
      console.error('Speaker submit error:', error.response?.data);
      console.error('Full error:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.response?.data?.message);
      const errorData = error.response?.data;
      if (errorData?.message && Array.isArray(errorData.message)) {
        errorData.message.forEach((msg: string) => {
          message.error(msg);
        });
      } else {
        message.error(errorData?.message || t('speakers.operationFailed'));
      }
    }
  };

  // Filter speakers based on search text
  const filteredSpeakers = speakers.filter((speaker) => {
    const searchLower = searchText.toLowerCase();
    return (
      speaker.name.toLowerCase().includes(searchLower) ||
      speaker.title?.toLowerCase().includes(searchLower) ||
      speaker.company?.toLowerCase().includes(searchLower) ||
      speaker.bio?.toLowerCase().includes(searchLower) ||
      speaker.Event?.title?.toLowerCase().includes(searchLower)
    );
  });

  const columns: ColumnsType<Speaker> = [
    {
      title: t('speakers.photoColumn'),
      dataIndex: 'photo',
      key: 'photo',
      width: 80,
      render: (photo: string, record: Speaker) => (
        <Avatar size={48} src={photo} icon={<UserOutlined />} alt={record.name} />
      ),
    },
    {
      title: t('speakers.order'),
      dataIndex: 'order',
      key: 'order',
      width: 90,
      // Az érték nélküli előadók a számozottak után jönnek, név szerint —
      // ugyanaz a rendezés, amit a résztvevői oldal is kap a szervertől.
      render: (order: number | null | undefined) =>
        order ?? <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('speakers.job_title'),
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => title || '-',
    },
    {
      title: t('speakers.company'),
      dataIndex: 'company',
      key: 'company',
      render: (company: string) => company || '-',
    },
    {
      title: t('common.event'),
      dataIndex: ['Event', 'title'],
      key: 'event',
      render: (title: string) => title || '-',
    },
    {
      title: t('speakers.contact'),
      key: 'contact',
      render: (_, record) => (
        <Space size="small">
          {record.email && (
            <a href={`mailto:${record.email}`} target="_blank" rel="noopener noreferrer">
              <MailOutlined />
            </a>
          )}
          {record.linkedin && (
            <a href={record.linkedin} target="_blank" rel="noopener noreferrer">
              <LinkedinOutlined />
            </a>
          )}
          {record.twitter && (
            <a
              href={`https://twitter.com/${record.twitter}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TwitterOutlined />
            </a>
          )}
        </Space>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Title level={2} style={{ margin: 0 }}>
              {t('speakers.title')}
            </Title>
            <Badge
              count={filteredSpeakers.length}
              style={{ backgroundColor: '#52c41a' }}
              showZero
              overflowCount={999}
            />
          </Space>
          <Space>
            <Search
              placeholder={t('speakers.searchPlaceholder')}
              allowClear
              style={{ width: 350 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={setSearchText}
            />
            <Select
              style={{ width: 250 }}
              placeholder={t('common.allEvents')}
              allowClear
              value={selectedEvent}
              onChange={setSelectedEvent}
            >
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('speakers.new_speaker')}
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredSpeakers}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) =>
              t('speakers.paginationTotal', { from: range[0], to: range[1], count: total }),
            showQuickJumper: true,
          }}
        />
      </Space>

      <Modal
        title={editingSpeaker ? t('speakers.edit_speaker') : t('speakers.new_speaker')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="eventId"
            label={t('common.event')}
            rules={[{ required: true, message: t('common.selectEventRequired') }]}
          >
            <Select placeholder={t('common.selectEvent')} disabled={!!editingSpeaker}>
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label={t('common.name')}
            rules={[{ required: true, message: t('common.nameRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="order" label={t('speakers.order')} extra={t('speakers.orderHint')}>
            <InputNumber min={0} precision={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="title" label={t('speakers.job_title')}>
            <Input placeholder={t('speakers.jobTitlePlaceholder')} />
          </Form.Item>

          <Form.Item name="company" label={t('speakers.company')}>
            <Input />
          </Form.Item>

          <Form.Item name="bio" label={t('speakers.bio')}>
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item name="photo" label={t('speakers.photo')}>
            {/* Feltöltés ÉS URL: a mező eddig csak szöveg volt, ezért a
                meglévő események külső hivatkozásokat tartalmazhatnak. */}
            <ImageUpload
              uploadUrl="/api/upload/image"
              fieldName="image"
              maxSize={5}
              allowUrlInput
              placeholder={t('speakers.photoPlaceholder')}
            />
          </Form.Item>

          <Form.Item name="email" label={t('common.email')}>
            <Input placeholder={t('speakers.emailPlaceholder')} />
          </Form.Item>

          <Form.Item name="linkedin" label={t('speakers.linkedin')}>
            <Input placeholder={t('speakers.linkedinPlaceholder')} />
          </Form.Item>

          <Form.Item name="twitter" label={t('speakers.twitter')}>
            <Input placeholder={t('speakers.twitterPlaceholder')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingSpeaker ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('speakers.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Speakers;
